/**
 * Announcement Bar (CB) - Enhanced Countdown System
 * Version: 1.0.0
 *
 * Features:
 * - Multiple scheduled announcements and countdowns
 * - Daily recurring countdown timers
 * - Desktop: Up to 2 messages side-by-side
 * - Mobile: Simple swap between messages
 * - No external dependencies (no jQuery, no Slick Slider)
 */

if (typeof CBAnnouncementBar === 'undefined') {

  /**
   * Main announcement bar custom element
   * Manages message scheduling, visibility, and mobile swapping
   */
  class CBAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.scheduler = null;
      this.mobileSwapper = null;
      this.updateInterval = null;
    }

    connectedCallback() {
      // Get settings
      this.mobileSwapSeconds = parseInt(this.dataset.mobileSwapSeconds) || 5;
      this.storeTimezone = this.dataset.storeTimezone || 'America/New_York';

      // Get all message blocks
      this.messagesContainer = this.querySelector('[data-messages-container]');
      this.messageBlocks = Array.from(this.querySelectorAll('.cb-announcement-bar__message'));

      // Initialize scheduler
      this.scheduler = new CountdownScheduler(this.messageBlocks, this.storeTimezone);

      // Update visible messages
      this.updateVisibleMessages();

      // Check for updates every 30 seconds
      this.updateInterval = setInterval(() => {
        this.updateVisibleMessages();
      }, 30000);

      // Initialize mobile swapper if needed
      if (window.innerWidth <= 768) {
        this.initMobileSwapper();
      }

      // Re-initialize mobile swapper on resize
      window.addEventListener('resize', this.handleResize.bind(this));

      // Make visible
      this.style.opacity = '1';
      this.style.visibility = 'visible';
    }

    disconnectedCallback() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
      }
      window.removeEventListener('resize', this.handleResize.bind(this));
    }

    /**
     * Update which messages are visible based on schedule
     */
    updateVisibleMessages() {
      const activeMessages = this.scheduler.getActiveMessages();

      if (activeMessages.length === 0) {
        // No active messages - hide entire bar
        this.remove();
        return;
      }

      // Hide all messages first
      this.messageBlocks.forEach(msg => {
        msg.style.display = 'none';
        msg.classList.remove('is-active');
      });

      // Show up to 2 active messages (for desktop side-by-side)
      const messagesToShow = activeMessages.slice(0, 2);
      messagesToShow.forEach(msg => {
        msg.style.display = 'flex';
        msg.classList.add('is-active');
      });

      // Update container class based on message count
      if (messagesToShow.length === 1) {
        this.messagesContainer.classList.add('single-message');
      } else {
        this.messagesContainer.classList.remove('single-message');
      }

      // Re-initialize mobile swapper if needed (only on mobile)
      const isMobile = window.innerWidth <= 768;
      if (isMobile && messagesToShow.length > 1) {
        if (this.mobileSwapper) {
          this.mobileSwapper.stop();
        }
        this.initMobileSwapper();
      } else if (!isMobile && this.mobileSwapper) {
        // Stop mobile swapper on desktop
        this.mobileSwapper.stop();
        this.mobileSwapper = null;
      }
    }

    /**
     * Initialize mobile message swapper
     */
    initMobileSwapper() {
      const activeMessages = Array.from(
        this.messageBlocks.filter(msg => msg.classList.contains('is-active'))
      );

      if (activeMessages.length > 1) {
        this.mobileSwapper = new MobileMessageSwapper(
          activeMessages,
          this.mobileSwapSeconds
        );
        this.mobileSwapper.start();
      }
    }

    /**
     * Handle window resize
     */
    handleResize() {
      const isMobile = window.innerWidth <= 768;
      const activeMessages = this.messageBlocks.filter(msg => msg.classList.contains('is-active'));

      if (isMobile && activeMessages.length > 1) {
        // Initialize mobile swapper
        if (this.mobileSwapper) {
          this.mobileSwapper.stop();
        }
        this.initMobileSwapper();
      } else {
        // Stop mobile swapper on desktop and show all active messages
        if (this.mobileSwapper) {
          this.mobileSwapper.stop();
          this.mobileSwapper = null;
        }
        // Show all active messages on desktop (up to 2)
        activeMessages.forEach(msg => {
          msg.style.display = 'flex';
        });
      }
    }

  }

  /**
   * Countdown Scheduler
   * Determines which messages should be visible based on current date/time
   */
  class CountdownScheduler {
    constructor(messageBlocks, storeTimezone) {
      this.messageBlocks = messageBlocks;
      this.storeTimezone = storeTimezone;
      this.countdownTimers = new Map();
      this.initializeCountdowns();
    }

    /**
     * Initialize countdown timers for countdown blocks
     */
    initializeCountdowns() {
      this.messageBlocks.forEach(block => {
        const blockType = block.dataset.blockType;
        const timerElement = block.querySelector('[data-countdown-timer]');

        if (!timerElement) return;

        if (blockType === 'scheduled_countdown') {
          // Scheduled countdown
          const endDate = block.dataset.endDate;
          const timer = new CountdownTimer(timerElement, endDate, this.storeTimezone);
          this.countdownTimers.set(block.id, timer);
        }
      });
    }

    /**
     * Get currently active messages
     */
    getActiveMessages() {
      const now = new Date();
      const activeMessages = [];

      for (const block of this.messageBlocks) {
        if (this.isMessageActive(block, now)) {
          activeMessages.push(block);

          // Start countdown timer if not already started
          const timer = this.countdownTimers.get(block.id);
          if (timer && !timer.isRunning) {
            timer.start();
          }
        } else {
          // Stop countdown timer if running
          const timer = this.countdownTimers.get(block.id);
          if (timer && timer.isRunning) {
            timer.stop();
          }
        }
      }

      return activeMessages;
    }

    /**
     * Check if a message block should be active right now
     */
    isMessageActive(block, now) {
      const blockType = block.dataset.blockType;

      if (blockType === 'scheduled_announcement' || blockType === 'scheduled_countdown') {
        // Check if current time is within date range
        const startDate = this.parseDate(block.dataset.startDate);
        const endDate = this.parseDate(block.dataset.endDate);

        return now >= startDate && now <= endDate;
      }

      return false;
    }

    /**
     * Parse date string to Date object
     */
    parseDate(dateString) {
      // Expected format: "YYYY-MM-DD HH:MM:SS"
      // Convert to ISO 8601 format for parsing
      const isoString = dateString.replace(' ', 'T');
      return new Date(isoString);
    }
  }

  /**
   * Countdown Timer
   * Updates countdown display every second
   */
  class CountdownTimer {
    constructor(element, endDateString, storeTimezone) {
      this.element = element;
      this.endDate = this.parseDate(endDateString);
      this.storeTimezone = storeTimezone;
      this.interval = null;
      this.isRunning = false;
      this.isMobile = window.innerWidth <= 768;
      this.countdownStyle = element.dataset.countdownStyle || 'boxes';
      this.previousValues = { days: null, hours: null, minutes: null, seconds: null };
    }

    /**
     * Start countdown
     */
    start() {
      if (this.isRunning) return;

      this.isRunning = true;
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    /**
     * Stop countdown
     */
    stop() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      this.isRunning = false;
    }

    /**
     * Update countdown display
     */
    update() {
      const now = new Date();
      const timeRemaining = this.endDate - now;

      if (timeRemaining <= 0) {
        this.onExpired();
        return;
      }

      const { days, hours, minutes, seconds } = this.calculateTime(timeRemaining);
      this.render(days, hours, minutes, seconds);
    }

    /**
     * Calculate time remaining
     */
    calculateTime(ms) {
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / 1000 / 60) % 60);
      const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));

      return { days, hours, minutes, seconds };
    }

    /**
     * Render countdown display
     */
    render(days, hours, minutes, seconds) {
      // Always render based on countdown style (not device type)
      switch (this.countdownStyle) {
        case 'flip':
          this.renderFlipFormat(days, hours, minutes, seconds);
          break;
        case 'colon':
          this.renderColonFormat(days, hours, minutes, seconds);
          break;
        case 'text':
          this.renderTextFormat(days, hours, minutes, seconds);
          break;
        case 'boxes':
        default:
          this.renderBoxesFormat(days, hours, minutes, seconds);
          break;
      }
    }

    /**
     * Render boxes format (00 d 00 h 00 m 00 s)
     */
    renderBoxesFormat(days, hours, minutes, seconds) {
      const html = `
        ${days > 0 ? `<span class="countdown-digit">${this.pad(days)}<span class="countdown-label">d</span></span>` : ''}
        <span class="countdown-digit">${this.pad(hours)}<span class="countdown-label">h</span></span>
        <span class="countdown-digit">${this.pad(minutes)}<span class="countdown-label">m</span></span>
        <span class="countdown-digit">${this.pad(seconds)}<span class="countdown-label">s</span></span>
      `;
      this.element.innerHTML = html;
    }

    /**
     * Render flip card format (animated flip cards)
     */
    renderFlipFormat(days, hours, minutes, seconds) {
      // Initialize flip cards on first render
      if (!this.element.querySelector('.flip-card')) {
        const html = `
          ${days > 0 ? `
            <div class="flip-card" data-unit="days">
              <div class="flip-card-wrapper">
                <div class="flip-card-digit">${this.pad(days)}</div>
              </div>
              <span class="flip-label">d</span>
            </div>
          ` : ''}
          <div class="flip-card" data-unit="hours">
            <div class="flip-card-wrapper">
              <div class="flip-card-digit">${this.pad(hours)}</div>
            </div>
            <span class="flip-label">h</span>
          </div>
          <div class="flip-card" data-unit="minutes">
            <div class="flip-card-wrapper">
              <div class="flip-card-digit">${this.pad(minutes)}</div>
            </div>
            <span class="flip-label">m</span>
          </div>
          <div class="flip-card" data-unit="seconds">
            <div class="flip-card-wrapper">
              <div class="flip-card-digit">${this.pad(seconds)}</div>
            </div>
            <span class="flip-label">s</span>
          </div>
        `;
        this.element.innerHTML = html;
      } else {
        // Update existing flip cards with animation
        this.updateFlipCard('days', days);
        this.updateFlipCard('hours', hours);
        this.updateFlipCard('minutes', minutes);
        this.updateFlipCard('seconds', seconds);
      }

      // Store current values for next comparison
      this.previousValues = { days, hours, minutes, seconds };
    }

    /**
     * Update a single flip card with animation
     */
    updateFlipCard(unit, value) {
      const card = this.element.querySelector(`.flip-card[data-unit="${unit}"]`);
      if (!card) return;

      const prevValue = this.previousValues[unit];
      const currentValue = this.pad(value);

      // Only animate if value changed
      if (prevValue !== null && prevValue !== value) {
        const wrapper = card.querySelector('.flip-card-wrapper');
        wrapper.classList.add('flipping');

        // Update digit after animation
        setTimeout(() => {
          const digit = card.querySelector('.flip-card-digit');
          digit.textContent = currentValue;
          wrapper.classList.remove('flipping');
        }, 300);
      } else if (prevValue === null) {
        // First render, just update value
        const digit = card.querySelector('.flip-card-digit');
        digit.textContent = currentValue;
      }
    }

    /**
     * Render colon format (00:00:00:00)
     */
    renderColonFormat(days, hours, minutes, seconds) {
      const parts = [];
      if (days > 0) parts.push(this.pad(days));
      parts.push(this.pad(hours));
      parts.push(this.pad(minutes));
      parts.push(this.pad(seconds));
      this.element.innerHTML = `<span class="countdown-colon">${parts.join(':')}</span>`;
    }

    /**
     * Render text format (0d 0h 0m 0s)
     */
    renderTextFormat(days, hours, minutes, seconds) {
      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      parts.push(`${hours}h`);
      parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);
      this.element.innerHTML = `<span class="countdown-text">${parts.join(' ')}</span>`;
    }

    /**
     * Pad number with leading zero
     */
    pad(num) {
      return num < 10 ? '0' + num : num;
    }

    /**
     * Handle countdown expiration
     */
    onExpired() {
      this.stop();
      // Trigger re-check of active messages
      const announcementBar = this.element.closest('cb-announcement-bar');
      if (announcementBar) {
        announcementBar.updateVisibleMessages();
      }
    }

    /**
     * Parse date string
     */
    parseDate(dateString) {
      const isoString = dateString.replace(' ', 'T');
      return new Date(isoString);
    }
  }

  /**
   * Mobile Message Swapper
   * Handles instant swapping between multiple messages on mobile
   */
  class MobileMessageSwapper {
    constructor(messages, swapInterval) {
      this.messages = messages;
      this.swapInterval = swapInterval * 1000; // Convert to milliseconds
      this.currentIndex = 0;
      this.interval = null;
    }

    /**
     * Start swapping
     */
    start() {
      if (this.messages.length <= 1) return;

      // Show first message
      this.showMessage(0);

      // Swap every X seconds
      this.interval = setInterval(() => {
        this.currentIndex = (this.currentIndex + 1) % this.messages.length;
        this.showMessage(this.currentIndex);
      }, this.swapInterval);
    }

    /**
     * Stop swapping
     */
    stop() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    /**
     * Show message at index
     */
    showMessage(index) {
      // Hide all messages
      this.messages.forEach(msg => {
        msg.style.display = 'none';
      });

      // Show current message
      if (this.messages[index]) {
        this.messages[index].style.display = 'flex';
      }
    }
  }

  // Define custom element
  customElements.define('cb-announcement-bar', CBAnnouncementBar);

  // Set global flag to prevent redefinition
  window.CBAnnouncementBar = CBAnnouncementBar;
}
